import React, { useState } from 'react';
import { Download, Loader2, FileText } from 'lucide-react';
import { useToast } from '../shared.jsx';
import { loadJsPDF } from './loadJsPdf.js';

// Format a date nicely
function fmt(dateStr) {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleString(); } catch { return dateStr; }
}
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString(); } catch { return dateStr; }
}

// Get participant name by ID from conflict
function getName(conflict, userId) {
  const m = conflict.mentees?.find(x => x.id === userId);
  if (m) return m.name || m.email || 'Participant';
  // Check mentor comments/messages for mentor identity (conflict.mentor is legacy, may not be set)
  if (conflict.mentor?.id === userId) return conflict.mentor.name || conflict.mentor.email || 'Mentor';
  const mentorComment = (conflict.mentorComments || []).find(c => c.userId === userId);
  if (mentorComment) return mentorComment.userName || 'Mentor';
  const mentorMsg = (conflict.mentorMessages || []).find(m => m.userId === userId && m.isMentorComment);
  if (mentorMsg) return mentorMsg.userName || 'Mentor';
  // Check audit trail
  const auditEntry = (conflict.auditLog || conflict.auditTrail || []).find(a => a.userId === userId);
  if (auditEntry) return auditEntry.userName || 'Unknown';
  return 'Unknown';
}

// Build chronological timeline entries from conflict data
function buildTimeline(conflict) {
  const events = [];
  const auditTime = (action, userId) => (conflict.auditLog || conflict.auditTrail || [])
    .find(entry => entry.action === action && (!userId || entry.userId === userId))?.timestamp;

  // 1. Conflict created
  events.push({
    time: conflict.createdAt,
    type: 'System',
    actor: getName(conflict, conflict.createdBy),
    action: 'Conflict created',
    detail: null
  });

  // 2. Terms acceptance
  const accepted = conflict.termsAcceptance?.acceptedBy || [];
  // We don't have per-user timestamps for acceptance, so use statusHistory
  const statusHistory = conflict.statusHistory || [];
  statusHistory.forEach(sh => {
    if (sh.status === 'identify-define' && sh.timestamp) {
      events.push({
        time: sh.timestamp,
        type: 'System',
        actor: 'All parties',
        action: 'Terms accepted, conflict moved to Identify & Define',
        detail: null
      });
    }
  });

  // 3. Identify & Define — paraphrases
  const idData = conflict.steps?.identifyDefine?.data;
  if (idData) {
    Object.entries(idData).forEach(([userId, data]) => {
      if (data.paraphrase) {
        events.push({
          time: data.timestamp || data.submittedAt,
          type: 'Identify & Define',
          actor: getName(conflict, userId),
          action: 'Submitted paraphrase',
          detail: data.paraphrase
        });
      }
      // Acknowledgments
      (data.acknowledgments || []).forEach(ack => {
        events.push({ time: ack.timestamp, type: 'Identify & Define',
          actor: ack.userName || getName(conflict, ack.userId),
          action: `Acknowledged ${getName(conflict, userId)}'s paraphrase`, detail: null });
      });
      if (data.acknowledgedBy) {
        Object.entries(data.acknowledgedBy).forEach(([ackUserId, ackData]) => {
          events.push({
            time: ackData.timestamp || ackData.acknowledgedAt,
            type: 'Identify & Define',
            actor: getName(conflict, ackUserId),
            action: `Acknowledged ${getName(conflict, userId)}'s paraphrase`,
            detail: ackData.comment || null
          });
        });
      }
    });
  }

  // 4. Challenges
  const challenges = conflict.steps?.identifyDefine?.challenges || [];
  challenges.forEach(ch => {
    events.push({
      time: ch.timestamp,
      type: 'Identify & Define',
      actor: ch.userName || getName(conflict, ch.userId),
      action: 'Documented challenge',
      detail: ch.text
    });
  });

  // 5. Skip communication votes
  const skipVotes = conflict.steps?.identifyDefine?.skipCommunicationVotes;
  if (skipVotes) {
    Object.entries(skipVotes).forEach(([userId, vote]) => {
      events.push({
        time: vote.timestamp,
        type: 'Identify & Define',
        actor: getName(conflict, userId),
        action: vote.skip ? 'Voted to skip Communication' : 'Voted to continue to Communication',
        detail: null
      });
    });
  }

  // 6. Communication cycles
  const cycles = conflict.steps?.communicate?.cycles || [];
  cycles.forEach((cycle, idx) => {
    if (cycle.statement) {
      events.push({
        time: cycle.timestamp || cycle.submittedAt,
        type: 'Communicate',
        actor: getName(conflict, cycle.userId),
        action: `Because statement (Round ${Math.floor(idx / 2) + 1})`,
        detail: cycle.statement
      });
    }
    if (cycle.paraphrase) {
      events.push({
        time: cycle.paraphraseTimestamp || cycle.paraphrasedAt,
        type: 'Communicate',
        actor: getName(conflict, cycle.paraphrasedBy || cycle.paraphraseUserId),
        action: `Paraphrased ${getName(conflict, cycle.userId)}'s statement`,
        detail: cycle.paraphrase
      });
    }
  });

  // 7. Satisfaction votes
  const satVotes = conflict.steps?.communicate?.satisfactionVotes;
  if (satVotes) {
    Object.entries(satVotes).forEach(([userId, vote]) => {
      events.push({
        time: vote.timestamp,
        type: 'Communicate',
        actor: getName(conflict, userId),
        action: vote.satisfied ? 'Voted satisfied, ready to move on' : 'Voted not yet satisfied',
        detail: null
      });
    });
  }

  // 8. Explore Alternatives
  const alternatives = conflict.steps?.exploreAlternatives?.alternatives || [];
  alternatives.forEach(alt => {
    events.push({
      time: alt.timestamp || alt.createdAt,
      type: 'Explore Alternatives',
      actor: getName(conflict, alt.createdBy),
      action: 'Proposed alternative',
      detail: alt.text || alt
    });
  });

  // 9. Evaluate & Select — ratings
  (conflict.auditLog || conflict.auditTrail || []).filter(entry => entry.action === 'Marked done proposing alternatives').forEach(entry => {
    events.push({ time: entry.timestamp, type: 'Explore Alternatives',
      actor: entry.userName || getName(conflict, entry.userId), action: 'Done proposing alternatives', detail: null });
  });
  const evalData = conflict.steps?.evaluateSelect?.ratings;
  if (evalData) {
    Object.entries(evalData).forEach(([userId, ratings]) => {
      events.push({
        time: ratings.timestamp || ratings.submittedAt || auditTime('Submitted solution ratings', userId),
        type: 'Evaluate & Select',
        actor: getName(conflict, userId),
        action: 'Submitted ratings for all alternatives',
        detail: alternatives.filter(alt => typeof ratings[alt.id] === 'number')
          .map(alt => `${alt.text}: ${ratings[alt.id]}/5`).join('\n') || null
      });
    });
  }

  // Selected resolution — `selected` stores the alternative ID; look up text from alternatives
  const selectedAltId = conflict.steps?.evaluateSelect?.selected;
  const alts = conflict.steps?.exploreAlternatives?.alternatives || [];
  const selectedAlt = selectedAltId ? alts.find(a => a.id === selectedAltId) : null;
  if (selectedAltId) {
    events.push({
      time: conflict.steps?.evaluateSelect?.selectedAt || statusHistory.find(entry => entry.status === 'agree-implement')?.timestamp || null,
      type: 'Evaluate & Select',
      actor: 'System',
      action: 'Resolution selected (highest-rated)',
      detail: conflict.actualOutcome || selectedAlt?.text || selectedAltId
    });
  }

  // Early resolution from Identify & Define
  const resCheck = conflict.steps?.identifyDefine?.resolutionCheck;
  if (resCheck) {
    events.push({
      time: resCheck.timestamp,
      type: 'Identify & Define',
      actor: getName(conflict, resCheck.proposedBy),
      action: 'Proposed early resolution',
      detail: resCheck.resolution
    });
  }

  // 10. Agree & Implement — acknowledgments (stored as array called `acknowledged`)
  const agreeData = conflict.steps?.agreeImplement?.acknowledged;
  if (Array.isArray(agreeData)) {
    agreeData.forEach(ack => {
      events.push({
        time: ack.timestamp,
        type: 'Agree & Implement',
        actor: ack.userName || getName(conflict, ack.userId),
        action: 'Formally acknowledged the resolution',
        detail: null
      });
    });
  }

  // 11. Follow-up ratings (stored as direct properties: processSatisfaction, feltHeard, partnerHeard)
  const followUpData = conflict.steps?.followUp?.ratings;
  if (followUpData) {
    Object.entries(followUpData).forEach(([userId, ratings]) => {
      const categoryLabels = { processSatisfaction: 'Process Satisfaction', feltHeard: 'Felt Heard', partnerHeard: 'Partner Heard' };
      const ratingDetails = Object.entries(categoryLabels)
        .filter(([key]) => typeof ratings[key] === 'number')
        .map(([key, label]) => `${label}: ${ratings[key]}/5`);
      events.push({
        time: ratings.timestamp || ratings.submittedAt,
        type: 'Follow-up',
        actor: ratings.userName || getName(conflict, userId),
        action: 'Submitted process ratings',
        detail: ratingDetails.length > 0 ? ratingDetails.join(', ') : null
      });
    });
  }

  // 12. Mentor inline comments (NOT private chat messages)
  const mentorComments = conflict.mentorComments || [];
  mentorComments.forEach(mc => {
    events.push({
      time: mc.timestamp,
      type: `Mentor Comment`,
      actor: mc.userName || getName(conflict, mc.userId),
      action: `Comment on ${mc.entryType || 'entry'}${mc.visibleTo?.length ? ` (visible to ${mc.visibleTo.length} participant${mc.visibleTo.length !== 1 ? 's' : ''})` : ''}`,
      detail: mc.text
    });
  });

  // 13. Status transitions from history
  statusHistory.forEach(sh => {
    if (sh.status && sh.status !== 'identify-define') {
      events.push({
        time: sh.timestamp,
        type: 'System',
        actor: sh.changedBy ? getName(conflict, sh.changedBy) : 'System',
        action: `Status changed to ${sh.status.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`,
        detail: null
      });
    }
  });

  // Sort chronologically, with fallback for missing timestamps
  events.sort((a, b) => {
    if (!a.time && !b.time) return 0;
    if (!a.time) return 1;
    if (!b.time) return -1;
    return new Date(a.time) - new Date(b.time);
  });

  return events;
}

// === PDF GENERATION ===
async function generateConflictPDF(conflict) {
  await loadJsPDF();
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth(); // 215.9mm
  const pageHeight = doc.internal.pageSize.getHeight(); // 279.4mm
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const DARK = [28, 25, 23];
  const GRAY = [87, 83, 78];
  const AMBER = [217, 119, 6];
  const LIGHT_BG = [254, 243, 199];
  const WHITE = [255, 255, 255];
  const HEADER_BG = [28, 25, 23];
  const LINE = [214, 211, 209];

  function checkPage(needed = 20) {
    if (y + needed > pageHeight - 25) {
      // Footer on current page
      addFooter();
      doc.addPage();
      y = margin;
      return true;
    }
    return false;
  }

  function addFooter() {
    const pageNum = doc.internal.getNumberOfPages();
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text(`Generated from The ArguMentor  |  theargumentor.com`, margin, pageHeight - 10);
    doc.text(`Page ${pageNum}  |  ${new Date().toLocaleString()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  // === HEADER ===
  doc.setFillColor(...HEADER_BG);
  doc.rect(0, 0, pageWidth, 38, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...WHITE);
  doc.text('Conflict Resolution Record', margin, 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(180, 180, 180);
  doc.text('The ArguMentor', margin, 22);

  // Title
  doc.setFontSize(12);
  doc.setTextColor(...WHITE);
  const titleText = conflict.title || 'Untitled Conflict';
  const titleLines = doc.splitTextToSize(titleText, contentWidth);
  doc.text(titleLines, margin, 31);

  y = 44;

  // === CONFLICT INFO TABLE ===
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...AMBER);
  doc.text('CONFLICT DETAILS', margin, y);
  y += 5;

  const participants = (conflict.mentees || []).map(m => `${m.name || 'Unknown'} (${m.email || '—'})`).join(', ');
  const status = (conflict.status || 'unknown').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Derive mentors/observers who interacted with this conflict (relationship-based, not assigned)
  const mentorUserIds = new Set();
  const mentorNames = [];
  (conflict.mentorComments || []).forEach(c => { if (c.userId && !mentorUserIds.has(c.userId)) { mentorUserIds.add(c.userId); mentorNames.push(c.userName || 'Mentor'); } });
  (conflict.mentorMessages || []).forEach(m => { if (m.isMentorComment && m.userId && !mentorUserIds.has(m.userId)) { mentorUserIds.add(m.userId); mentorNames.push(m.userName || 'Mentor'); } });
  if (conflict.mentor?.id && !mentorUserIds.has(conflict.mentor.id)) { mentorNames.push(conflict.mentor.name || 'Mentor'); }
  const mentorDisplay = mentorNames.length > 0 ? mentorNames.join(', ') : 'No mentor interactions recorded';

  const infoRows = [
    ['Status', status],
    ['Created', fmt(conflict.createdAt)],
    ['Participants', participants],
    ['Mentor(s)', mentorDisplay],
  ];

  if (conflict.needByDate) {
    infoRows.push(['Need-by Date', fmtDate(conflict.needByDate)]);
  }

  infoRows.forEach(([label, value]) => {
    checkPage(7);
    doc.setFillColor(248, 248, 247);
    doc.rect(margin, y - 3.5, 42, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...DARK);
    doc.text(label, margin + 2, y);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY);
    const valLines = doc.splitTextToSize(value, contentWidth - 46);
    doc.text(valLines, margin + 44, y);
    y += Math.max(7, valLines.length * 4);
  });

  // Access note
  y += 2;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(6.5);
  doc.setTextColor(140, 140, 140);
  const accessNote = 'Note: Mentor access is determined by established relationships with participants, not direct assignment to the conflict. The mentor(s) listed above are those who interacted with this conflict through comments or messages.';
  const accessLines = doc.splitTextToSize(accessNote, contentWidth);
  doc.text(accessLines, margin, y);
  y += accessLines.length * 3 + 4;

  // === PROBLEM STATEMENT ===
  checkPage(30);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...AMBER);
  doc.text('PROBLEM STATEMENT', margin, y);
  y += 5;

  const ps = conflict.problemStatement || {};
  const psFields = [
    ['Who', ps.who],
    ['What', ps.what],
    ['Where', ps.where],
    ['When', ps.when],
    ['How', ps.how],
    ['Desired Outcome', conflict.desiredOutcome || ps.desiredOutcome],
  ];

  psFields.forEach(([label, value]) => {
    if (!value) return;
    checkPage(12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...DARK);
    doc.text(`${label}:`, margin, y);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY);
    const lines = doc.splitTextToSize(value, contentWidth - 2);
    doc.text(lines, margin, y + 4);
    y += 4 + (lines.length * 3.5) + 3;
  });

  // === ACTUAL RESOLUTION (below desired outcome) ===
  const earlySelectedId = conflict.steps?.evaluateSelect?.selected;
  const earlyAlts = conflict.steps?.exploreAlternatives?.alternatives || [];
  const earlySelectedAlt = earlySelectedId ? earlyAlts.find(a => a.id === earlySelectedId) : null;
  const earlyResCheck = conflict.steps?.identifyDefine?.resolutionCheck;
  const earlyResolution = conflict.actualOutcome || earlySelectedAlt?.text || earlyResCheck?.resolution;

  if (earlyResolution) {
    checkPage(20);
    y += 2;
    const resText = typeof earlyResolution === 'string' ? earlyResolution : JSON.stringify(earlyResolution);
    const resLines = doc.splitTextToSize(resText, contentWidth - 10);
    const resBlockHeight = 10 + (resLines.length * 3.5);

    doc.setFillColor(...LIGHT_BG);
    doc.rect(margin, y - 2, contentWidth, resBlockHeight, 'F');
    doc.setDrawColor(...AMBER);
    doc.setLineWidth(0.5);
    doc.line(margin, y - 2, margin, y + resBlockHeight - 2);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...AMBER);
    doc.text('ACTUAL RESOLUTION', margin + 4, y + 2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...DARK);
    doc.text(resLines, margin + 4, y + 7);
    y += resBlockHeight + 4;
  }

  y += 4;

  // === CHRONOLOGICAL TIMELINE ===
  checkPage(20);
  doc.setFillColor(...AMBER);
  doc.rect(margin, y - 1, contentWidth, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...WHITE);
  doc.text('CHRONOLOGICAL RECORD', margin + 3, y + 3.5);
  y += 12;

  const events = buildTimeline(conflict);

  if (events.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text('No recorded events yet.', margin, y);
    y += 8;
  }

  let lastPhase = '';
  events.forEach(ev => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    const actionLines = doc.splitTextToSize(ev.actor + ': ' + ev.action, contentWidth - 38);
    const detailLines = ev.detail ? doc.splitTextToSize(ev.detail, contentWidth - 10) : [];
    const newPhase = ev.type !== lastPhase;
    const headingHeight = newPhase ? 11 : 0;
    // Keep a phase heading with the action and the start of its detail.
    checkPage(headingHeight + actionLines.length * 3.5 + Math.min(detailLines.length, 2) * 3.5 + 7);
    if (newPhase) {
      y += 2;
      doc.setDrawColor(...LINE);
      doc.line(margin, y, margin + contentWidth, y);
      y += 4;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...AMBER);
      doc.text(ev.type.toUpperCase(), margin, y);
      y += 5;
      lastPhase = ev.type;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...GRAY);
    doc.text(ev.time ? fmt(ev.time) : 'Time not recorded', margin, y);
    actionLines.forEach(line => {
      checkPage(5);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...DARK);
      doc.text(line, margin + 38, y);
      y += 3.5;
    });
    y += 1;
    detailLines.forEach(line => {
      checkPage(5);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...GRAY);
      doc.text(line, margin + 5, y);
      y += 3.5;
    });
    y += 4;
  });

  // === RESOLUTION SUMMARY ===
  // actualOutcome is set at conflict root when all parties acknowledge in Agree & Implement
  // selected is the alternative ID stored in evaluateSelect; look up text from alternatives array
  const selectedId = conflict.steps?.evaluateSelect?.selected;
  const alternatives = conflict.steps?.exploreAlternatives?.alternatives || [];
  const selectedAltObj = selectedId ? alternatives.find(a => a.id === selectedId) : null;
  const resCheck = conflict.steps?.identifyDefine?.resolutionCheck;
  const resolution = conflict.actualOutcome || selectedAltObj?.text || resCheck?.resolution;
  const evalRatings = conflict.steps?.evaluateSelect?.ratings;
  const agreeAcked = conflict.steps?.agreeImplement?.acknowledged;

  if (resolution) {
    const resText = typeof resolution === 'string' ? resolution : JSON.stringify(resolution);
    const resLines = doc.splitTextToSize(resText, contentWidth - 10);

    // Calculate average score for selected alternative
    let avgScore = null;
    if (selectedId && evalRatings) {
      const scores = Object.values(evalRatings).map(r => r[selectedId]).filter(s => typeof s === 'number');
      if (scores.length > 0) avgScore = (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
    }

    // Collect who acknowledged (stored as array)
    const ackNames = [];
    if (Array.isArray(agreeAcked)) {
      agreeAcked.forEach(ack => {
        ackNames.push({ name: ack.userName || getName(conflict, ack.userId), time: ack.timestamp });
      });
    }

    const blockLines = resLines.length;
    const scoreLineHeight = avgScore ? 5 : 0;
    const ackLineHeight = ackNames.length > 0 ? 5 + (ackNames.length * 4) : 0;
    const blockHeight = 12 + (blockLines * 3.5) + scoreLineHeight + ackLineHeight;

    checkPage(blockHeight + 8);
    y += 4;
    doc.setFillColor(...LIGHT_BG);
    doc.rect(margin, y - 2, contentWidth, blockHeight, 'F');
    doc.setDrawColor(...AMBER);
    doc.setLineWidth(0.5);
    doc.line(margin, y - 2, margin, y + blockHeight - 2);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...AMBER);
    doc.text('AGREED RESOLUTION', margin + 4, y + 2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...DARK);
    doc.text(resLines, margin + 4, y + 7);
    let resY = y + 7 + (blockLines * 3.5) + 2;

    if (avgScore) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...GRAY);
      doc.text(`Average rating: ${avgScore}/5`, margin + 4, resY);
      resY += 5;
    }

    if (ackNames.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...AMBER);
      doc.text('Formally acknowledged by:', margin + 4, resY);
      resY += 4;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...GRAY);
      ackNames.forEach(ack => {
        doc.text(`• ${ack.name}${ack.time ? ', ' + fmt(ack.time) : ''}`, margin + 6, resY);
        resY += 4;
      });
    }

    y += blockHeight + 4;
  }

  // === FOLLOW-UP RATINGS SUMMARY ===
  const followUpRatings = conflict.steps?.followUp?.ratings;
  if (followUpRatings && Object.keys(followUpRatings).length > 0) {
    const summaryHeight = 12 + Object.keys(followUpRatings).length * 17 + 23;
    checkPage(Math.min(summaryHeight, pageHeight - 25 - margin));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...AMBER);
    doc.text('FOLLOW-UP: PROCESS RATINGS', margin, y);
    y += 3;

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6.5);
    doc.setTextColor(...GRAY);
    doc.text('Each participant rated the resolution process across several categories (1-5 scale).', margin, y);
    y += 5;

    // Rating categories stored as direct properties
    const categoryLabels = { processSatisfaction: 'Process Satisfaction', feltHeard: 'Felt Heard', partnerHeard: 'Partner Heard' };
    const categoryKeys = Object.keys(categoryLabels);

    // Collect all categories and compute averages
    const allCategories = {};
    const raterCount = Object.keys(followUpRatings).length;

    Object.entries(followUpRatings).forEach(([userId, data]) => {
      categoryKeys.forEach(cat => {
        if (typeof data[cat] === 'number') {
          if (!allCategories[cat]) allCategories[cat] = [];
          allCategories[cat].push(data[cat]);
        }
      });
    });

    // Per-participant ratings
    Object.entries(followUpRatings).forEach(([userId, data]) => {
      checkPage(18);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...DARK);
      doc.text(data.userName || getName(conflict, userId), margin + 2, y);
      y += 4;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...GRAY);
      categoryKeys.forEach(cat => {
        if (typeof data[cat] === 'number') {
          doc.text(`${categoryLabels[cat]}: ${data[cat]}/5`, margin + 4, y);
          y += 3.5;
        }
      });
      y += 2;
    });

    // Overall averages (if more than one rater)
    if (raterCount > 1 && Object.keys(allCategories).length > 0) {
      checkPage(23);
      y += 2;
      doc.setFillColor(...LIGHT_BG);
      const avgBlockHeight = 6 + Object.keys(allCategories).length * 3.5;
      doc.rect(margin, y - 2, contentWidth, avgBlockHeight, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...AMBER);
      doc.text('AVERAGE SCORES', margin + 4, y + 2);
      y += 6;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...DARK);
      Object.entries(allCategories).forEach(([cat, scores]) => {
        const avg = (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
        const label = categoryLabels[cat] || cat;
        doc.text(`${label}: ${avg}/5 (${scores.length} rating${scores.length !== 1 ? 's' : ''})`, margin + 4, y);
        y += 3.5;
      });
      y += 4;
    }
  }

  // Add footer to last page
  addFooter();

  // Save
  const filename = `Conflict_Record_${(conflict.title || 'export').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 40)}.pdf`;
  doc.save(filename);
  return filename;
}

// === EXPORT BUTTON COMPONENT ===
function ConflictExportButton({ conflict, compact = false }) {
  const [exporting, setExporting] = useState(false);
  const toast = useToast();

  const handleExport = async () => {
    setExporting(true);
    try {
      const filename = await generateConflictPDF(conflict);
      toast(`Download requested: ${filename}`);
    } catch (e) {
      console.error('Export error:', e);
      toast('Failed to generate export. Please try again.');
    }
    setExporting(false);
  };

  if (compact) {
    return (
      <button
        onClick={handleExport}
        disabled={exporting}
        className="flex items-center gap-1.5 text-sm text-stone-700 hover:text-stone-800 bg-stone-50 hover:bg-stone-100 disabled:bg-stone-50 px-3 py-1.5 rounded-lg border border-stone-200 transition-colors"
        title="Export conflict record as PDF"
      >
        {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
        Export Record
      </button>
    );
  }

  return (
    <button
      onClick={handleExport}
      disabled={exporting}
      className="flex items-center gap-2 px-4 py-2 bg-stone-700 hover:bg-stone-800 disabled:bg-stone-400 text-white text-sm rounded-lg font-medium transition-colors shadow-sm"
      title="Export conflict record as PDF"
    >
      {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      Export Conflict Record
    </button>
  );
}

export { ConflictExportButton, generateConflictPDF, buildTimeline };
export default ConflictExportButton;
